import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~common/react/CommonStyles';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { cssClass, flexRowStyle } from '../../../style';

const cssPrefix = 'PageHeader';

const ninetyPercentWhite = 'rgba(255, 255, 255, 0.9)';
const twentytyPercentWhite = 'rgba(255, 255, 255, 0.2)';
const vizViewerLinkColor = ninetyPercentWhite;
const vizViewerLinkActiveColor = TabStyles.Colors.White;

export namespace PageHeaderStyles {
  export namespace CssVariables {
    export const headerTextColorBase: string = '--header-text-color-base';
    export const headerTextColor: string = '--header-text-color';
    export const headerTextColorTwentyPercent: string = '--header-text-color-twenty-percent';
    export const headerTextColorNinetyPercent: string = '--header-text-color-ninety-percent';
    export const headerLinkColor: string = '--header-link-color';
    export const headerLinkActiveColor: string = '--header-link-active-color';
    export const headerBackgroundHoverColor: string = '--header-background-hover-color';
    export const headerHeight: string = '--header-height';
    export const headerButtonRightMargin: string = '--header-button-right-margin';
    export const headerButtonSize: string = '--header-button-size';
    export const headerIconSize: string = '--header-icon-size';

    export function cssVar(name: string, defaultVal?: any) {
      if(defaultVal !== undefined && defaultVal !== null) {
        return `var(${name}, ${defaultVal})`;
      } else {
        return `var(${name})`;
      }
    }
  }
  import cssVar = PageHeaderStyles.CssVariables.cssVar;


  export const headerHeight = cssVar(CssVariables.headerHeight, `52px`);
  export const headerButtonSize = cssVar(CssVariables.headerButtonSize, '32px');

  export const ContainerStyles = {
    display: 'flex',
    height: headerHeight,
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
  export const Container = cssClass(cssPrefix, 'container', ContainerStyles);

  export const InfoContainerStyles: React.CSSProperties = {
    display: 'flex',
    height: '48px',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#15557F',
    marginTop: '-48px',
    animationName: 'fadein',
    animationDuration: '0.5s',
    animationDelay: '2s',
    animationFillMode: 'forwards'
  };

  export const InfoContainer = cssClass(cssPrefix, 'infocontainer', InfoContainerStyles);

  export const infoContainerAnimationProp = `@keyframes fadein {
    from {
      margin-top: -48px;
    }
    to {
      margin-top: 0px;
    }
  }`;

  const derivedThemeColors = {
    [CssVariables.headerTextColor]: `rgb(${cssVar(CssVariables.headerTextColorBase)})`,
    [CssVariables.headerTextColorTwentyPercent]: `rgba(${cssVar(CssVariables.headerTextColorBase)}, 0.2)`,
    [CssVariables.headerTextColorNinetyPercent]: `rgba(${cssVar(CssVariables.headerTextColorBase)}, 0.9)`,
  };

  // Remove when removing vizToolbarRedesignEnabled feature flag
  export const darkHeaderTheme = cssClass(cssPrefix, 'dark-page-header-theme', {
    backgroundColor: TabStyles.Colors.ApplicationBlue,
    [CssVariables.headerTextColorBase]: `255, 255, 255`, //base is used to derive colors with different opacity
    [CssVariables.headerLinkColor]: vizViewerLinkColor,
    [CssVariables.headerLinkActiveColor]: vizViewerLinkActiveColor,
    [CssVariables.headerBackgroundHoverColor]: 'inherit',
    [CssVariables.headerButtonRightMargin]: '16px',
    [CssVariables.headerIconSize]: '24px',
    ...derivedThemeColors,
  });

  export const shortHeaderTheme = cssClass(cssPrefix, 'short-page-header-theme', {
    [CssVariables.headerTextColorBase]: `79, 79, 79`,
    [CssVariables.headerLinkColor]: '#1a699e',
    [CssVariables.headerLinkActiveColor]: '#15557f',
    [CssVariables.headerBackgroundHoverColor]: '#ebebeb',
    [CssVariables.headerButtonRightMargin]: '12px',
    [CssVariables.headerButtonSize]: '32px',
    [CssVariables.headerIconSize]: '18px',
    ...derivedThemeColors,
  });

  export const regularHeaderTheme = cssClass(cssPrefix, 'regular-page-header-theme', {
    [CssVariables.headerTextColorBase]: `79, 79, 79`,
    [CssVariables.headerLinkColor]: '#1a699e',
    [CssVariables.headerLinkActiveColor]: '#15557f',
    [CssVariables.headerBackgroundHoverColor]: '#ebebeb',
    [CssVariables.headerButtonRightMargin]: '16px',
    [CssVariables.headerButtonSize]: '32px',
    [CssVariables.headerIconSize]: '24px',
    ...derivedThemeColors,
  });

  export const offWhiteBackgroundColor = cssClass(cssPrefix, 'off-white-background-color', {
    backgroundColor: '#fafafa',
  });

  // we have a left region and right region so that we do not override the link styling inside the
  // alerts panel
  export const LeftRegionVizViewer = cssClass(cssPrefix, 'left-region-viz-viewer', {
    height: '100%',
    color: cssVar(CssVariables.headerTextColor),
    backgroundColor: 'inherit',
    $nest: {  // for breadcrumbs
      'a': {
        color: cssVar(CssVariables.headerLinkColor),
        $nest: {
          '&:hover': {
            color: cssVar(CssVariables.headerLinkActiveColor)
          },
          '&:active': {
            color: cssVar(CssVariables.headerLinkActiveColor)
          }
        }
      }
    }
  });

  export const RightRegionVizViewer = cssClass(cssPrefix, 'right-region-viz-viewer', {
    height: '100%',
    // Since our page header isn't responsive the right region can expand beyond
    // the Pageheader width, so this ensures the page header background stays application blue
    backgroundColor: 'inherit'
  });

  export const Region = cssClass(cssPrefix, 'region', flexRowStyle, {
    height: '100%',
    marginLeft: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
    marginRight: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
    alignItems: 'center',
  });

  export const RightItem = cssClass(cssPrefix, 'right-item', {
    display: 'flex',
    alignItems: 'center',
    $nest: {
      '&:not(:last-of-type)': {
        marginRight: cssVar(CssVariables.headerButtonRightMargin)
      }
    }
  });

  export const LeftItem = cssClass(cssPrefix, 'left-item', {
    display: 'flex',
    alignItems: 'center',
    $nest: {
      '&:not(:last-of-type)': {
        marginRight: 6
      }
    }
  });

  export const NavPanelButton = cssClass(cssPrefix, 'nav-panel-button', {
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  });

  const dividerHeight = 20;
  export const VizViewerDivider = cssClass(cssPrefix, 'viz-viewer-divider', {
    width: 1,
    height: dividerHeight,
    marginRight: 11,  // the sapce between the divider and the eyeglass needs to be 20. The eyeglass
                      // icon has 9 px of padding, so we only need 11 here
    backgroundColor: `rgba(${cssVar(CssVariables.headerTextColorBase)}, .6)`
  });

  export const VizViewerButtons = cssClass(cssPrefix, 'viz-viewer-buttons', {
    ...CommonStyles.Layout.FlexBlockLeftAndCenter,
    fontWeight: 'normal',
  });

  export const BackButton: React.CSSProperties = {
    cursor: 'pointer',
    width: headerButtonSize,
    height: headerButtonSize,
    marginRight: 16
  };

  export const ButtonFocus: React.CSSProperties = CommonStyles.Focus.Indicator;

  export const FavoriteButtonFocusClass = cssClass(cssPrefix, 'favorite-button-focus', {
    $nest: {
      '&:focus': {
        ...ButtonFocus,
      }
    },
  });

  export const BackButtonWrapperDiv: React.CSSProperties = {
    marginTop: 0
  };

  export const TextButtonStyle: React.CSSProperties = {
    color: cssVar(CssVariables.headerTextColorNinetyPercent),
    padding: TabStyles.Spacing.DefaultSpace,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    marginRight: '0px'
  };

  export const TextButton = cssClass(cssPrefix, 'text-button', {
    ...TextButtonStyle,
    $nest: {
      '&:hover': {
        color: cssVar(CssVariables.headerTextColor),
        backgroundColor: cssVar(CssVariables.headerTextColorTwentyPercent)
      }
    }
  });

  export const backgroundHover = cssClass(cssPrefix, 'header-hover-background', {
    borderRadius: 2,
    $nest: {
      '&:hover': {
        backgroundColor: cssVar(CssVariables.headerBackgroundHoverColor)
      }
    }
  });

  export const iconForTextButton: React.CSSProperties = {
    verticalAlign: 'middle',
    paddingRight: TabStyles.Spacing.DefaultSpace,
    paddingBottom: '1px',
    cursor: 'pointer'
  };

  export const iconOnlyButton: React.CSSProperties = {
    ...iconForTextButton,
    paddingRight: '0px',
  };

  // This is so the links inside the data connections tooltip (on viz pages) can have the blue outline focus
  export const dataConnectionsTooltipClass = cssClass(cssPrefix, 'dataconnection-tooltip-links', {
    margin: 12,
    minWidth: 200,
    $nest: {
      'a': {
        width: 'calc(100% - 2px)',
        height: '19px',
        lineHeight: '17px',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        boxShadow: '0 0 4px transparent',
      },
      'a:focus': CommonStyles.Focus.Indicator
    }
  });

  export const VizViewerWidgetsBarStyle = cssClass('VizViewerWidgetsBar', 'container', {
    display: 'flex',
    flexWrap: 'nowrap',
    marginRight: cssVar(CssVariables.headerButtonRightMargin),
  });

  export const DevicePreviewButtonContainer = cssClass('VizViewerWidgetsBar', 'devicePreviewButtonContainer', {
    marginRight: cssVar(CssVariables.headerButtonRightMargin),
    lineHeight: PageHeaderStyles.headerHeight,
  });
}
